import { CheckCircle2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";

import { GRAVUN_BRAND } from "@/branding";
import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";

interface PublicEstimate {
  title: string;
  estimate_number: number;
  status: string;
  business_name: string;
  issued_at: string | null;
  valid_until: string | null;
  expired: boolean;
  customer_notes: string | null;
  terms: string | null;
  subtotal_cents: number;
  discount_cents: number;
  tax_cents: number;
  total_cents: number;
  deposit_percent_bp: number | null;
  deposit_cents: number | null;
  accepted_at: string | null;
  accepted_signature: string | null;
  line_items: {
    description: string;
    quantity: number;
    unit_price_cents: number;
    line_total_cents: number;
  }[];
}

const money = (cents: number) =>
  (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

const day = (value: string | null) =>
  value
    ? new Date(value).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "";

/**
 * Public, unauthenticated estimate acceptance page (/#/e/:token).
 *
 * The token IS the authorization: both RPCs are security definer and take
 * nothing else, so a customer sees exactly one estimate and never a draft.
 * Accepting types a name (the electronic signature), which the server
 * stores with a timestamp. Layout is single-column and touch-sized because
 * most customers open this on a phone.
 */
export const PublicEstimatePage = () => {
  const { token = "" } = useParams();
  const [estimate, setEstimate] = useState<PublicEstimate | null | "missing">(
    null,
  );
  const [signature, setSignature] = useState("");
  const [acceptDeposit, setAcceptDeposit] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState<{
    accepted_at: string;
    deposit_invoice_total_cents: number | null;
  } | null>(null);

  useEffect(() => {
    getSupabaseClient()
      .rpc("get_public_estimate", { p_token: token })
      .then(
        ({ data }) => setEstimate((data as PublicEstimate) ?? "missing"),
        () => setEstimate("missing"),
      );
  }, [token]);

  const accept = async () => {
    setSubmitting(true);
    setError(null);
    const { data, error: rpcError } = await getSupabaseClient().rpc(
      "accept_public_estimate",
      {
        p_token: token,
        p_signature: signature,
        p_accept_deposit: acceptDeposit,
      },
    );
    setSubmitting(false);
    if (rpcError) {
      setError(rpcError.message);
      return;
    }
    setAccepted(
      data as {
        accepted_at: string;
        deposit_invoice_total_cents: number | null;
      },
    );
  };

  if (estimate === null) return null;

  if (estimate === "missing") {
    return (
      <Shell>
        <div className="rounded-2xl border border-border-subtle bg-card p-8 text-center">
          <h1 className="text-xl font-semibold">
            This estimate is not available
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            The link may be wrong, or the estimate has not been sent yet. Please
            contact the business that sent it to you.
          </p>
        </div>
      </Shell>
    );
  }

  const alreadyAccepted = Boolean(accepted) || estimate.status === "accepted";

  return (
    <Shell business={estimate.business_name}>
      <div className="flex flex-col gap-4">
        <div className="rounded-2xl border border-border-subtle bg-card p-6 sm:p-8">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">
              {estimate.title}
            </h1>
            <span className="text-[13px] text-muted-foreground">
              Estimate #{estimate.estimate_number}
            </span>
          </div>
          {estimate.issued_at ? (
            <p className="mt-1 text-[13px] text-muted-foreground">
              Issued {day(estimate.issued_at)}
              {estimate.valid_until
                ? `, valid until ${day(estimate.valid_until)}`
                : ""}
            </p>
          ) : null}

          <div className="mt-5 flex flex-col gap-2">
            {estimate.line_items.map((item, index) => (
              <div
                key={`${item.description}-${index}`}
                className="flex items-start justify-between gap-4 border-b border-border-subtle pb-2 last:border-0"
              >
                <span className="text-[14px]">
                  {item.description}
                  {item.quantity !== 1 ? (
                    <span className="text-muted-foreground">
                      {" "}
                      x {item.quantity}
                    </span>
                  ) : null}
                </span>
                <span className="shrink-0 text-[14px] tabular-nums">
                  {money(item.line_total_cents)}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-4 flex flex-col gap-1 text-[14px]">
            <Row label="Subtotal" value={money(estimate.subtotal_cents)} />
            {estimate.discount_cents > 0 ? (
              <Row
                label="Discount"
                value={`-${money(estimate.discount_cents)}`}
              />
            ) : null}
            {estimate.tax_cents > 0 ? (
              <Row label="Tax" value={money(estimate.tax_cents)} />
            ) : null}
            <div className="mt-1 flex items-center justify-between border-t border-border pt-2 text-[17px] font-semibold">
              <span>Total</span>
              <span className="tabular-nums">
                {money(estimate.total_cents)}
              </span>
            </div>
          </div>

          {estimate.customer_notes ? (
            <p className="mt-4 whitespace-pre-wrap text-[13.5px] text-muted-foreground">
              {estimate.customer_notes}
            </p>
          ) : null}
        </div>

        {alreadyAccepted ? (
          <div className="rounded-2xl border border-border-subtle bg-card p-6 text-center sm:p-8">
            <CheckCircle2 className="mx-auto size-10 text-success" />
            <h2 className="mt-3 text-xl font-semibold">Accepted, thank you</h2>
            <p className="mt-1 text-[14px] text-muted-foreground">
              Signed by {accepted ? signature : estimate.accepted_signature} on{" "}
              {day(accepted?.accepted_at ?? estimate.accepted_at)}.
            </p>
            {accepted?.deposit_invoice_total_cents ? (
              <p className="mt-2 text-[14px]">
                A deposit invoice for{" "}
                {money(accepted.deposit_invoice_total_cents)} is on its way to
                you; the work is scheduled once it is paid.
              </p>
            ) : (
              <p className="mt-2 text-[14px] text-muted-foreground">
                The team will be in touch to schedule the work.
              </p>
            )}
          </div>
        ) : (
          <div className="rounded-2xl border border-border-subtle bg-card p-6 sm:p-8">
            {estimate.expired ? (
              <p className="text-[14px] text-destructive">
                This estimate expired on {day(estimate.valid_until)}. Please
                contact the business for an updated price.
              </p>
            ) : estimate.status === "declined" ? (
              <p className="text-[14px] text-muted-foreground">
                This estimate was declined. Contact the business if that was a
                mistake.
              </p>
            ) : (
              <>
                <h2 className="text-lg font-semibold">Accept this estimate</h2>
                {estimate.deposit_cents ? (
                  <label className="mt-3 flex items-start gap-2 rounded-md border border-border-subtle p-3 text-[14px]">
                    <input
                      type="checkbox"
                      className="mt-0.5 size-4 accent-primary"
                      checked={acceptDeposit}
                      onChange={(e) => setAcceptDeposit(e.target.checked)}
                    />
                    <span>
                      I agree to the {(estimate.deposit_percent_bp ?? 0) / 100}%
                      deposit of {money(estimate.deposit_cents)} to schedule the
                      work. You will receive an invoice for it.
                    </span>
                  </label>
                ) : null}

                <label className="mt-4 flex flex-col gap-1 text-[13px] font-medium">
                  Type your full name to sign
                  <input
                    className="h-11 w-full rounded-[10px] border border-input bg-card px-3 text-[15px] outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    placeholder="Jane Smith"
                    autoComplete="name"
                    value={signature}
                    onChange={(e) => setSignature(e.target.value)}
                  />
                </label>
                <p className="mt-2 text-[12px] text-muted-foreground">
                  Typing your name and pressing Accept is your electronic
                  signature. We record the name and the time you accepted.
                </p>

                {error ? (
                  <p className="mt-3 text-[13px] text-destructive">{error}</p>
                ) : null}

                <Button
                  size="lg"
                  className="mt-4 w-full"
                  disabled={submitting || signature.trim().length < 2}
                  onClick={accept}
                >
                  {submitting ? "Accepting…" : "Accept estimate"}
                </Button>
              </>
            )}
          </div>
        )}

        {estimate.terms ? (
          <div className="rounded-2xl border border-border-subtle bg-card p-6">
            <h3 className="text-[13px] font-semibold uppercase tracking-wide text-muted-foreground">
              Terms
            </h3>
            <p className="mt-2 whitespace-pre-wrap text-[13px] text-muted-foreground">
              {estimate.terms}
            </p>
          </div>
        ) : null}
      </div>
    </Shell>
  );
};

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-center justify-between text-muted-foreground">
    <span>{label}</span>
    <span className="tabular-nums">{value}</span>
  </div>
);

const Shell = ({
  business,
  children,
}: {
  business?: string;
  children: React.ReactNode;
}) => (
  <div className="min-h-screen bg-background">
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 px-4 py-6 sm:py-10">
      <div className="flex items-center justify-between">
        <img
          className="h-7 w-auto dark:hidden"
          src={GRAVUN_BRAND.wordmarkNavy}
          alt={GRAVUN_BRAND.name}
        />
        {business ? (
          <span className="text-[13px] text-muted-foreground">{business}</span>
        ) : null}
      </div>
      {children}
      <p className="pb-6 text-center text-[12px] text-muted-foreground">
        © {new Date().getFullYear()} {GRAVUN_BRAND.name} Inc.
      </p>
    </div>
  </div>
);

PublicEstimatePage.path = "/e/:token";
